import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Bot, Send, X, Loader2, Sparkles, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface SafetyCopilotProps {
  siteId?: string | null;
  companyId?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export const SafetyCopilot = ({ siteId, companyId, isOpen, onClose }: SafetyCopilotProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hi! I'm your AI Safety Copilot. I have access to all your safety data including reports, risk scores, friction points, interventions, and worker metrics. Ask me anything about your organization's safety status!",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));

      const { data, error } = await supabase.functions.invoke('safety-copilot-query', {
        body: { 
          query: userMessage, 
          siteId,
          companyId,
          conversationHistory 
        }
      });

      if (error) {
        // Check for AI limit exceeded (429)
        if (error.message?.includes('limitExceeded') || error.message?.includes('AI_LIMIT_EXCEEDED')) {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: '⚠️ Batas percakapan AI bulanan sudah tercapai. Hubungi admin untuk upgrade atau top-up.',
            timestamp: new Date()
          }]);
          return;
        }
        throw error;
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response || 'Sorry, I could not generate a response.',
        timestamp: new Date()
      }]);
    } catch (error: any) {
      console.error('Error querying copilot:', error);
      
      // Handle 429 from response data
      if (error?.context?.body && typeof error.context.body === 'string') {
        try {
          const body = JSON.parse(error.context.body);
          if (body.limitExceeded) {
            setMessages(prev => [...prev, { 
              role: 'assistant', 
              content: '⚠️ Batas percakapan AI bulanan sudah tercapai. Hubungi admin untuk upgrade atau top-up.',
              timestamp: new Date()
            }]);
            return;
          }
        } catch {}
      }
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearConversation = () => {
    setMessages([{
      role: 'assistant',
      content: "Conversation cleared. I'm ready to help with your safety questions!",
      timestamp: new Date()
    }]);
  };

  const suggestedQueries = [
    "What's our current safety status?",
    "Show me the top friction points",
    "Which teams have the highest risk?",
    "How effective are our interventions?",
    "What are the most common hazard types?",
    "Give me a weekly safety briefing"
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "fixed z-50 bg-card border shadow-2xl rounded-2xl overflow-hidden flex flex-col",
            isExpanded 
              ? "inset-4 md:inset-8" 
              : "bottom-24 right-6 w-[420px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-120px)]"
          )}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-foreground/20 rounded-xl">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">AI Safety Copilot</h3>
                  <p className="text-xs text-primary-foreground/80">
                    {siteId ? 'Site-specific data' : 'All company data'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/20"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/20"
                  onClick={clearConversation}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/20"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground text-center">Try asking:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestedQueries.map((query, i) => (
                    <Badge 
                      key={i}
                      variant="secondary"
                      className="cursor-pointer hover:bg-secondary/80 transition-colors"
                      onClick={() => {
                        setInput(query);
                      }}
                    >
                      {query}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={cn(
                  "flex",
                  message.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-3",
                  message.role === 'user' 
                    ? "bg-primary text-primary-foreground rounded-br-md" 
                    : "bg-muted rounded-bl-md"
                )}>
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-3 w-3 text-primary" />
                      <span className="text-xs font-medium text-primary">AI Copilot</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                  <p className={cn(
                    "text-[10px] mt-2",
                    message.role === 'user' ? "text-primary-foreground/60" : "text-muted-foreground"
                  )}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </motion.div>
            ))}

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Analyzing data...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-background/50">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about safety metrics, risks, trends..."
                className="flex-1 bg-muted/50 border-0 focus-visible:ring-1"
                disabled={isLoading}
              />
              <Button 
                onClick={handleSend} 
                disabled={!input.trim() || isLoading}
                className="shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SafetyCopilot;
